import { isDefined } from '@sgme/fp';

import type {
  FlexiForwardReplyMessagePremiumData,
  FxoHedge,
  ReceivedFlexiForwardQuoteReply,
} from '@/store/flexi-forward/api/requestFlexiForwardRfs.model.ts';
import type { FlexiForwardQuote } from '@/store/flexi-forward/state.ts';
import type { QuoteWithPrice } from '@/utils/rfs/type.ts';
import { toPredealCheck } from '@/utils/rfs/utils.ts';

export function createFlexiForwardQuote(
  tileId: string,
  message: QuoteWithPrice<ReceivedFlexiForwardQuoteReply>,
): FlexiForwardQuote {
  const { Timestamp: timestamp, RequestId: requestId, OptionQuoteReply: quoteReply } = message;

  const legsData = quoteReply.LegsData[tileId];
  const { QuoteId: quoteId, QuoteType: quoteType, RfsWindow: rfsWindow, Hedge: hedge } = quoteReply;

  const premiumData: FlexiForwardReplyMessagePremiumData | undefined = legsData?.PremiumData;
  const greeksData = legsData?.Greeks;

  const { Ccy1 } = quoteReply;

  const hedgeData = hedge[0];
  const hedgeValue: FxoHedge | undefined = isDefined(hedgeData)
    ? {
        hedgeType: 'Spot',
        maturityDate: hedgeData.MaturityDate,
        notionalAmount: hedgeData.NotionalAmount,
        notionalCurrencyIndex: hedgeData.NotionalCurrency === Ccy1 ? 1 : 2,
        price: hedgeData.RateMid,
        way: hedgeData.Way,
      }
    : undefined;

  return {
    requestId,
    backTimestamp: timestamp,
    contribution: quoteReply.QuoteContribution ?? 'Manual',
    tiering: quoteReply.DistributionStream ?? 'STREAMDEFAULT',
    frontTimestamp: message.workerTimestamp,
    quoteId,
    quoteType,
    rfsWindow,
    forwardRate: legsData?.Solving ?? '',
    premiumData: {
      bidAmountCcy1: premiumData?.BidAmountCcy1 ?? '',
      bidAmountCcy2: premiumData?.BidAmountCcy2 ?? '',
      askAmountCcy1: premiumData?.AskAmountCcy1 ?? '',
      askAmountCcy2: premiumData?.AskAmountCcy2 ?? '',
      askPercentCcy1: premiumData?.AskPercentCcy1 ?? '',
      askPercentCcy2: premiumData?.AskPercentCcy2 ?? '',
    },
    greeks: {
      deltaCcy1: greeksData?.DeltaCcy1 ?? '',
      deltaCcy2: greeksData?.DeltaCcy2 ?? '',
    },
    marginData: {
      bidMarkupInBp: legsData?.MarginData?.BidMarkupInBp ?? '',
      askMarkupInBp: legsData?.MarginData?.AskMarkupInBp ?? '',
      markupCcy1: legsData?.MarginData?.AskMarkupCcy1 ?? '',
      markupCcy2: legsData?.MarginData?.AskMarkupCcy2 ?? '',
    },
    hedge: hedgeValue,
    predealChecks: quoteReply.PredealChecks.map(toPredealCheck),
  };
}
